import { useEffect, useMemo, useState } from 'react';
import { hole, merke, useMerkState, useRollstelle } from './merkplatz.ts';
import { api, datum, euro, heuteIso, type Anmelder } from '../api.ts';
import { Karte, Leer } from './Geruest.tsx';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';
import { Empfaengerwahl } from './Empfaengerwahl.tsx';
import { Suchfeld, SuchHinweis, useVerzoegert } from './Schluesselsuche.tsx';
import { Prueffenster } from './Rechnungspruefung.tsx';
import { fragen } from './rueckfrage.tsx';

/* NUR GEGEN GEPRÜFTE RECHNUNGEN ZAHLEN (.358). ORIGINALTEXT MARVIN (14.10.2026)
     „Bei denen, die dann da nicht geprüft sind, da müsste man dann
      draufklicken können, um auf den Bereich zu kommen, wo der Abgleich ist,
      wo ich es prüfen kann, damit ich ja wirklich nachher nur gegen geprüfte
      Rechnungen bezahle."
   Geprüft: Haken gesetzt, in Schritt 2 freigegeben oder abgelegt (dieselbe
   Regel wie `istGeprueft` im Server). Eine nicht geprüfte Rechnung lässt
   sich nicht anhaken; ihr „Prüfen" öffnet das Prüffenster. */
export const istGeprueft = (r: any) => !!r.geprueft || r.ablage === 'abgelegt' || r.ablage === 'freigegeben';

/* RECHNUNGEN DER PARTNER BEZAHLEN, AUCH GEBÜNDELT (.259)
 * ============================================================================
 * ORIGINALTEXT MARVIN (30.09.2026)
 *   „die gleiche funktion setzen wir auch bei der bezahung der rechnungen von
 *    den partnern ein. hier soll es aber möglich sein, rechnungen zu bündeln,
 *    sozusagen als sammelrechnung zu überweisen."
 *
 * Je Partner eine Gruppe. Angehakte Rechnungen EINES Partners werden EINE
 * Überweisung; der Verwendungszweck nennt alle Nummern. Alle angehakten
 * zusammen gehen als EIN Entwurf an Revolut. Nur mit `rechnung.bezahlen`. */

/* JEDE NUMMER STEHT IM VERWENDUNGSZWECK (09.10.2026). Marvin: „Es müssen
   immer alle rechnungsnummern zu lesen sein. Wenn sie nicht alle in einen
   Entwurf passen musst du sie in zwei aufteilen." Dieselbe Teilung wie
   `sammelzwecke` im Server: höchstens 140 Zeichen je Überweisung. */
function zwecke(nummern: string[]): string[] {
  const n = nummern.filter(Boolean);
  if (!n.length) return [];
  const text = (l: string[]) => (l.length === 1 ? 'Rechnung ' : 'Rechnungen ') + l.join(', ');
  const teile: string[] = [];
  let offen: string[] = [];
  for (const x of n) {
    if (offen.length && text([...offen, x]).length > 140) { teile.push(text(offen)); offen = [x]; }
    else offen.push(x);
  }
  if (offen.length) teile.push(text(offen));
  return teile;
}


/** Der Unterschied in Prozent der Rechnungssumme. */
function prozent(diff: number, basis: number): string {
  if (!basis) return '';
  const p = Math.abs(diff) / Math.abs(basis) * 100;
  if (p > 0 && p < 0.01) return 'unter 0,01 %';
  return p.toFixed(2).replace('.', ',') + ' %';
}

/** Wo eine Rechnung steht, in einem Wort (.300). */
function ZustandMarke({ r }: { r: any }) {
  if (r.bezahlt_am) return <span className="marke-zustand z-erledigt">bezahlt {datum(r.bezahlt_am)}</span>;
  if (r.abgelehnt_grund) return <span className="marke-zustand z-ueberfaellig" title={r.abgelehnt_grund}>abgelehnt</span>;
  if (r.dublette_von) return <span className="marke-zustand z-unterbrochen">Dublette</span>;
  if (r.zahlung_zustand && r.zahlung_zustand !== 'abgebrochen') return <span className="marke-zustand z-neu">in Revolut</span>;
  if (r.geklaert_am && !r.zahlbar) return <span className="marke-zustand z-erledigt">abgeschlossen</span>;
  if (r.zahlbar) return <span className="marke-zustand z-laufend">offen{r.freigegeben_am ? `, freigegeben ${datum(r.freigegeben_am)}` : ''}</span>;
  if (r.ablage === 'pruefung') return <span className="marke-zustand z-unterbrochen">wartet auf Freigabe</span>;
  return <span className="marke-zustand z-unterbrochen">nicht zahlbar</span>;
}

export function Partnerzahlung({ ich }: { ich: Anmelder }) {
  /* ZURÜCK AUS DER AKTE AN DIESELBE STELLE (.479). */
  const [d, setD0] = useState<{ gruppen: any[]; ueberweisungen: any[]; revolut: any; vorschlaege: any[]; dubletten: any[]; ohne_treffer?: any[]; ohne_skonto?: any[]; zuletzt_abgleich_am: string | null } | null>(() => hole('zahlung.daten') ?? null);
  const setD = (n: any) => { merke('zahlung.daten', n); setD0(n); };
  /* DIE KACHELN OBEN SIND FILTER (.260). */
  const [filter, setFilter] = useMerkState<'alle' | 'ueberfaellig' | 'revolut' | 'vorschlaege'>('zahlung.filter', 'alle');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [bestaetigen, setBestaetigen] = useState<any | null>(null);
  const [bezahlt, setBezahlt] = useState<string[] | null>(null);
  const [abschluss, setAbschluss] = useState<string[] | null>(null);
  /* MEHRFACHAUSWAHL BEI DEN DUBLETTEN (.269). Marvin: „es fehlt mir auch die mehrfachselektion." */
  const [dwahl, setDwahl] = useState<Set<string>>(new Set());
  const [pruefe, setPruefe] = useMerkState<string | null>('zahlung.pruefe', null);

  /* DIE SUCHE (.300): KLS-ID, SM, Anschrift, Rechnungsnummer, Firma, über
     ALLE Rechnungen der Partner, auch bezahlte und abgeschlossene. */
  const [firmenWahl, setFirmenWahl] = useMerkState<Set<string>>('zahlung.firmen', () => new Set());
  const [suche, setSuche] = useMerkState('zahlung.suche', '');
  useRollstelle('abrechnung.schritt3', !!d);
  const sucheV = useVerzoegert(suche);
  const [treffer, setTreffer] = useState<any[] | null>(null);
  async function suchen() {
    if (!sucheV.trim()) { setTreffer(null); return; }
    try { setTreffer((await api.partnerSuche(sucheV)).zeilen); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { suchen(); }, [sucheV]);

  async function laden() {
    try { setD(await api.partnerzahlung()); setFehler(null); } catch (f) { setFehler(ausFehler(f)); }
    if (sucheV.trim()) await suchen();
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  async function tu(k: string, f: () => Promise<string | null>) {
    setLaeuft(k); setFehler(null); setHinweis(null);
    try { const t = await f(); if (t) setHinweis(t); await laden(); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(null); }
  }
  const heuteTag = heuteIso();
  /* Wer sucht, sieht unten nur die offenen Treffer; bezahlen geht wie immer. */
  const trefferIds = treffer ? new Set(treffer.map((t) => t.id)) : null;
  const alleGruppen = (d?.gruppen ?? []).map((g) => trefferIds
    ? { ...g, rechnungen: g.rechnungen.filter((r: any) => trefferIds.has(r.id)) } : g)
    .map((g) => trefferIds ? { ...g, summe: Math.round(g.rechnungen.reduce((s: number, r: any) => s + (Number(r.zahlbetrag ?? r.betrag) || 0), 0) * 100) / 100 } : g)
    .filter((g) => !trefferIds || g.rechnungen.length);
  /* NACH FIRMEN WÄHLEN (.469). ORIGINALTEXT MARVIN (23.10.2026): „fehlt mir
     hier in der Zahlung die Möglichkeit, direkt oben zu filtern nach den
     einzelnen Firmen ... alle können so blockweise untereinander stehen, aber
     ich möchte noch selektieren, welche Firmen." Nichts gewählt heißt alle. */
  const gruppenGefiltert = firmenWahl.size ? alleGruppen.filter((g) => firmenWahl.has(String(g.subunternehmer_id ?? g.firma))) : alleGruppen;
  /* Was ausgeblendet ist, ist auch nicht gewählt: sonst ginge es unsichtbar in den Zahlungslauf. */
  useEffect(() => {
    if (!firmenWahl.size) return;
    const sichtbar = new Set(gruppenGefiltert.flatMap((g) => g.rechnungen.map((r: any) => r.id)));
    setGewaehlt((alt) => { const n = new Set([...alt].filter((id) => sichtbar.has(id))); return n.size === alt.size ? alt : n; });
  }, [firmenWahl]); // eslint-disable-line react-hooks/exhaustive-deps
  const gruppen = filter === 'ueberfaellig'
    ? gruppenGefiltert.map((g) => ({ ...g, rechnungen: g.rechnungen.filter((r: any) => r.faellig_am && r.faellig_am < heuteTag) }))
        .map((g) => ({ ...g, summe: Math.round(g.rechnungen.reduce((s: number, r: any) => s + (Number(r.zahlbetrag ?? r.betrag) || 0), 0) * 100) / 100 }))
        .filter((g) => g.rechnungen.length)
    : gruppenGefiltert;
  const alleOffen = alleGruppen.flatMap((g) => g.rechnungen);
  const ueber = alleOffen.filter((r: any) => r.faellig_am && r.faellig_am < heuteTag);
  /* .480: gezahlt wird der Betrag nach Skonto. */
  const summeVon = (l: any[]) => l.reduce((s: number, r: any) => s + (Number(r.zahlbetrag ?? r.betrag) || 0), 0);
  const vorschlaege = d?.vorschlaege ?? [];
  const alle = gruppen.flatMap((g) => g.rechnungen);
  const auswahl = alle.filter((r: any) => gewaehlt.has(r.id));
  const summe = auswahl.reduce((s: number, r: any) => s + (Number(r.zahlbetrag ?? r.betrag) || 0), 0);
  /* EINE RECHNUNG IST EINE ÜBERWEISUNG, KEINE SAMMELÜBERWEISUNG (.263). Je Firma
     geht eine Überweisung hinaus; „Sammel" ist sie erst ab 2 Rechnungen derselben Firma. */
  const jeFirma = new Map<string, number>();
  for (const r of auswahl) { const k = String(r.subunternehmer_id ?? r.id); jeFirma.set(k, (jeFirma.get(k) ?? 0) + 1); }
  const ueberweisungenZahl = jeFirma.size;
  const mitSammel = [...jeFirma.values()].some((n) => n > 1);
  const knopfText = ueberweisungenZahl > 1 ? `Als ${ueberweisungenZahl} Überweisungen an Revolut`
    : mitSammel ? 'Als Sammelüberweisung an Revolut' : 'Als Überweisung an Revolut';
  const heute = heuteIso();
  const umschalten = (ids: string[], an: boolean) => setGewaehlt((g) => {
    const n = new Set(g); for (const id of ids) { if (an) n.add(id); else n.delete(id); } return n;
  });
  const entwuerfe = (d?.ueberweisungen ?? []).filter((u) => u.zustand === 'entwurf');
  const zuletzt = (d?.ueberweisungen ?? []).filter((u) => u.zustand !== 'entwurf');
  const bereitFuerRevolut = !!d?.revolut?.bereit && auswahl.length > 0;
  const gesamt = useMemo(() => gruppen.reduce((s, g) => s + g.summe, 0), [d]);

  const kacheln: [typeof filter, string, number, number, string][] = [
    ['alle', 'offen', alleOffen.length, summeVon(alleOffen), ''],
    ['ueberfaellig', 'überfällig', ueber.length, summeVon(ueber), 'rot'],
    ['revolut', 'in Revolut', entwuerfe.length, entwuerfe.reduce((s, u) => s + (Number(u.betrag) || 0), 0), 'blau'],
    ['vorschlaege', 'aus dem Konto zu prüfen', vorschlaege.length, vorschlaege.reduce((s: number, v: any) => s + (Number(v.betrag) || 0), 0), 'warnung'],
  ];

  return (
    <>
    <div className="freigabekacheln">
      {kacheln.map(([f, t, n, sum, farbe]) => (
        <button key={f} type="button" aria-pressed={filter === f}
                className={`freigabekachel ${farbe} ${filter === f ? 'an' : ''}`} onClick={() => setFilter(f)}>
          <span className="zahl">{n}</span><span>{t}</span><span className="klein leise">{euro(sum)}</span>
        </button>
      ))}
    </div>

    <div className="reihe" style={{ margin: '4px 0 12px', alignItems: 'center', gap: 10 }}>
      <Suchfeld wert={suche} setze={setSuche} mehr="Rechnungsnr., Firma" breit={360} />
      {suche.trim() && <span className="klein leise">sucht in allen Rechnungen, auch bezahlten und abgeschlossenen</span>}
    </div>
    {alleGruppen.length > 1 && (
      <div className="firmenwahl">
        <span className="klein leise">Firmen:</span>
        <button type="button" className={'knopf klein' + (!firmenWahl.size ? ' an' : '')} onClick={() => setFirmenWahl(new Set())}>Alle</button>
        {alleGruppen.map((g) => {
          const k = String(g.subunternehmer_id ?? g.firma);
          const an = firmenWahl.has(k);
          return (
            <button key={k} type="button" className={'knopf klein' + (an ? ' an' : '')}
                    onClick={() => setFirmenWahl((alt) => { const n = new Set(alt); if (n.has(k)) n.delete(k); else n.add(k); return n; })}>
              {g.firma ?? 'ohne Firma'} <span className="leise">, {g.rechnungen.length}, {euro(g.summe)}</span>
            </button>
          );
        })}
      </div>
    )}
    {treffer && (
      <Karte titel={`Suche in allen Rechnungen der Partner (${treffer.length})`}>
        <SuchHinweis suche={sucheV} n={treffer.length} />
        {!treffer.length ? <Leer text="Keine Rechnung passt dazu." /> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="liste klein">
              <thead><tr><th>Firma</th><th>Rechnung</th><th>Baustelle</th><th>KLS</th><th>SM</th><th style={{ textAlign: 'right' }}>Betrag</th><th>Zustand</th></tr></thead>
              <tbody>
                {treffer.map((r) => (
                  <tr key={r.id}>
                    <td>{r.firma ?? 'Firma?'}</td>
                    <td>{r.hat_datei
                      ? <a href={api.rechnungAdresse(r.id, r.dateiname)} target="_blank" rel="noreferrer">{r.rechnungsnummer ?? 'ohne Nummer'}</a>
                      : (r.rechnungsnummer ?? 'ohne Nummer')}
                      {r.rechnungsdatum ? <span className="leise"> vom {datum(r.rechnungsdatum)}</span> : null}</td>
                    <td>{r.auftrag_id && r.anschrift
                          ? <a href={`#/akte/${r.auftrag_id}`} title="Auftrag öffnen">{r.anschrift}</a>
                          : (r.anschrift ?? <span className="leise">ohne Auftrag</span>)}</td>
                    <td>{r.kls_id ?? ''}</td>
                    <td>{r.sm_nummer ?? ''}</td>
                    <td style={{ textAlign: 'right' }}>{r.betrag != null ? euro(r.betrag) : ''}</td>
                    <td><ZustandMarke r={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
    )}

    {(filter === 'vorschlaege' || vorschlaege.length > 0) && (
      <Karte titel={`Aus dem Konto zu prüfen (${vorschlaege.length})`}>
        <p className="klein leise" style={{ marginTop: 0 }}>Diese Überweisungen nennen offene Rechnungen, passen aber nicht eindeutig. Übernehmen heißt: die genannten Rechnungen sind bezahlt, mit dem Tag aus dem Konto.</p>
        {!vorschlaege.length && <Leer text="Nichts zu prüfen." />}
        {vorschlaege.map((v: any) => <Vorschlag key={v.umsatz_id} v={v} laeuft={!!laeuft}
          los={(uebernehmen, ids) => tu('v' + v.umsatz_id, async () => {
            if (!uebernehmen) { await api.partnerVorschlag(v.umsatz_id, false); return 'Verworfen.'; }
            const r = await api.partnerVorschlag(v.umsatz_id, true, ids); return `${r.bezahlt} Rechnungen als bezahlt markiert.`;
          })} />)}
      </Karte>
    )}

    {/* WAS AN EINEN PARTNER GING UND KEINE RECHNUNG TRAF (.265), mit Grund. */}
    {(d?.ohne_treffer ?? []).length > 0 && (
      <Karte titel={`Überweisungen ohne Treffer (${d!.ohne_treffer!.length})`}>
        <details>
          <summary className="klein">Überweisungen an die IBAN eines Partners, zu denen keine offene Rechnung passt. Meist ist die Rechnung nicht in der Plattform oder die Nummer im Verwendungszweck anders geschrieben.</summary>
          <table>
            <thead><tr><th>Tag</th><th>Partner</th><th>Verwendungszweck</th><th>Grund</th><th className="zahl">Betrag</th></tr></thead>
            <tbody>
              {d!.ohne_treffer!.map((x: any) => (
                <tr key={x.umsatz_id}>
                  <td>{datum(x.datum)}</td><td>{x.firma ?? x.gegenueber}</td>
                  <td className="klein">{x.zweck}</td><td className="klein leise">{x.grund}</td>
                  <td className="zahl">{euro(x.betrag)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </Karte>
    )}

    {(d?.dubletten ?? []).length > 0 && (
      <Karte titel={`Doppelt erfasst (${d!.dubletten.length})`}
             rechts={<div className="reihe" style={{ gap: 6 }}>
               <button className="knopf klein" onClick={() => setDwahl(dwahl.size === d!.dubletten.length ? new Set() : new Set(d!.dubletten.map((x: any) => x.id)))}>
                 {dwahl.size === d!.dubletten.length ? 'Auswahl leeren' : 'Alle wählen'}</button>
               <button className="knopf klein" disabled={!!laeuft || !dwahl.size}
                       onClick={async () => {
                         const ids = [...dwahl];
                         if (!await fragen(`${ids.length} doppelte Rechnung${ids.length === 1 ? '' : 'en'} löschen? Die bezahlte bleibt jeweils (bei gleichem Betrag mit dem PDF der gelöschten), und die gelöschte kommt nicht wieder.`)) return;
                         tu('dmehr', async () => {
                           let n = 0; const fehlt: string[] = [];
                           for (const id of ids) {
                             try { await api.rechnungLoeschen(id, 'Dublette laut Kontoabgleich'); n++; }
                             catch (f: any) { fehlt.push(f?.message ?? String(f)); }
                           }
                           setDwahl(new Set());
                           return `${n} Dublette${n === 1 ? '' : 'n'} gelöscht.` + (fehlt.length ? ` Nicht gelöscht: ${fehlt.join('; ')}` : '');
                         });
                       }}>{laeuft === 'dmehr' ? 'Löscht …' : `Gewählte löschen (${dwahl.size})`}</button>
             </div>}>
        <p className="klein leise" style={{ marginTop: 0 }}>Dieselbe Rechnungsnummer steht mehrfach in der Liste. Laut Konto wurde die untere („Bezahlt") überwiesen; die obere („Doppelt") wird gelöscht. Trägt nur die obere das PDF und stimmt der Betrag, geht es auf die bezahlte über.</p>
        {d!.dubletten.map((x: any) => {
          const abw = x.behalten_betrag ? Math.abs(Number(x.betrag) - Number(x.behalten_betrag)) / Number(x.behalten_betrag) : 0;
          return (
          <div key={x.id} className="zahlgruppe">
            <div className="zahlgruppenkopf">
              <label className="zahlwahl" style={{ flex: '1 1 auto' }}>
                <input type="checkbox" checked={dwahl.has(x.id)} aria-label={`Dublette ${x.rechnungsnummer}`}
                       onChange={(e) => setDwahl((s) => { const n = new Set(s); if (e.target.checked) n.add(x.id); else n.delete(x.id); return n; })} />
                <div>
                <div className="freigabefirma">{x.rechnungsnummer}</div>
                <div className="klein">Doppelt: {x.firma ?? 'ohne Firma'}, {euro(x.betrag)}{x.rechnungsdatum ? `, vom ${datum(x.rechnungsdatum)}` : ''}
                  {x.hat_datei && <>, <a href={api.rechnungAdresse(x.id, x.dateiname)} target="_blank" rel="noreferrer">{x.dateiname ?? 'PDF'}</a></>}</div>
                <div className="klein">Bezahlt: {x.behalten_firma ?? 'ohne Firma'}, {euro(x.behalten_betrag)}{x.behalten_datum ? `, vom ${datum(x.behalten_datum)}` : ''}{x.behalten_bezahlt_am ? `, bezahlt am ${datum(x.behalten_bezahlt_am)}` : ''}
                  {x.behalten_hat_datei ? <>, <a href={api.rechnungAdresse(x.behalten_id, x.behalten_dateiname)} target="_blank" rel="noreferrer">{x.behalten_dateiname ?? 'PDF'}</a></>
                    : x.hat_datei && abw <= 0.1 ? <span className="leise">, ohne PDF, bekommt das PDF der oberen</span> : null}</div>
                {x.dublette_grund && <div className="klein leise">{x.dublette_grund}</div>}
                {abw > 0.1 && <div className="klein leise">Die Beträge weichen um {Math.round(abw * 100)} % ab: die obere ist vermutlich die alte, korrigierte Fassung und geht mit ihrem PDF. Ist die bezahlte nur eine Teilzahlung, „keine Dublette".</div>}
                </div>
              </label>
              <div className="reihe" style={{ gap: 6 }}>
                <button className="knopf klein" disabled={!!laeuft}
                        onClick={async () => { if (await fragen(`Die doppelte ${x.rechnungsnummer} (${euro(x.betrag)}) löschen? Die bezahlte bleibt${x.hat_datei && !x.behalten_hat_datei && abw <= 0.1 ? ' und bekommt das PDF' : ''}, und die gelöschte kommt nicht wieder.`))
                          tu('d' + x.id, async () => { const r = await api.rechnungLoeschen(x.id, 'Dublette laut Kontoabgleich'); return r.klartext ?? 'Gelöscht.'; }); }}>Löschen</button>
                <button className="knopf klein" disabled={!!laeuft}
                        onClick={() => tu('d' + x.id, async () => { await api.partnerKeineDublette(x.id); return 'Zurück unter „zu bezahlen".'; })}>keine Dublette</button>
              </div>
            </div>
          </div>
          );
        })}
      </Karte>
    )}

    <Karte titel={`Zu bezahlen (${alle.length}${alle.length ? ', ' + euro(gesamt) : ''})`}
           rechts={d?.revolut?.bereit ? (<div className="reihe" style={{ gap: 8, alignItems: 'center' }}>
             {d.revolut.saldo && <span className={`merker ${auswahl.length && summe > d.revolut.saldo.betrag ? 'fehler' : 'gruen'}`}>Kontostand {euro(d.revolut.saldo.betrag)}</span>}
             <button className="knopf klein" disabled={!!laeuft}
                     title={d.zuletzt_abgleich_am ? `Zuletzt abgeglichen: ${datum(d.zuletzt_abgleich_am)}` : 'Noch nie abgeglichen'}
                     onClick={() => tu('konto', async () => {
                       const r = await api.partnerKontoabgleich();
                       return `${r.umsaetze} Umsätze gelesen: ${r.bezahlt} Rechnungen und ${r.gebuehren} Gebühren als bezahlt markiert, ${r.vorschlaege} Vorschläge zum Prüfen.`;
                     })}>{laeuft === 'konto' ? 'Liest das Konto …' : 'Konto abgleichen'}</button>
             <button className="knopf klein" disabled={!!laeuft}
                     onClick={() => tu('abgleich', async () => {
                       const r = await api.partnerAbgleichen();
                       return r.geprueft ? `${r.geprueft} Überweisungen geprüft, ${r.bezahlt} bezahlt, ${r.zurueck} zurück.` : 'Kein offener Entwurf bei Revolut.';
                     })}>Bei Revolut nachsehen</button></div>
           ) : <span className="klein leise">Revolut ist nicht verbunden (einrichten unter „Gebühren bezahlen", oben rechts).</span>}>
      {/* DIE ANTWORT STEHT IN DER LEISTE, DIE MAN SIEHT (.264). Marvin: „wenn ich
          "als Sammelüberweisung an revolut" anklicke, dann kommt da nichts."
          Die Meldung stand oben in der Karte, weit außerhalb des Bildes, wenn
          man unten in der Liste anhakt. Die Leiste klebt oben und trägt sie jetzt. */}
      {/* DER FÄLLIGKEITS-PUSH (.414). Marvin, 19.10.2026: „dann fehlen mir immer
          noch die Push-Nachrichten als Erinnerung an mich der
          Zahlungsfälligkeit." Der Lauf lief, kam aber nie an, weil die
          Empfängeradresse zu keinem Zugang passte; das stand nur in der
          Automatik. Hier steht sein letzter Befund, rot wenn nichts hinausging. */}
      {d?.faellig_push && (() => {
        const f = d.faellig_push;
        const gut = f.zuletzt_ok && /Push an/.test(String(f.zuletzt_meldung ?? ''));
        const nichts = /Nichts fällig/.test(String(f.zuletzt_meldung ?? ''));
        /* ES IST EIN TELEGRAM-PUSH (.416). Marvin, 20.10.2026: „Warum will er
           hier an eine E-Mail-Adresse pushen? Er soll das per Telegram pushen."
           Tut er; seit .418 an jeden Zugang mit dem Recht rechnung.faelligkeit_push. Der Kasten sagt das jetzt,
           und „Jetzt senden" stößt den Lauf sofort an. */
        return (
          <div className={`hinweis ${gut || nichts ? '' : 'fehler'}`} style={{ marginBottom: 10 }}>
            <div className="reihe" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>
                <b>Telegram-Push zu fälligen Rechnungen</b> (täglich {String(f.uhrzeit ?? '').slice(0, 5)}{f.aktiv ? '' : ', ausgeschaltet'}, an jeden mit dem Recht „Fällige Partnerrechnungen per Telegram bekommen" und Telegram-Kennung im Zugang):{' '}
                {f.zuletzt_am ? `zuletzt ${datum(f.zuletzt_am)}, ` : 'noch nie gelaufen, '}{f.zuletzt_meldung ?? ''}
              </span>
              <button type="button" className="knopf klein" disabled={!!laeuft}
                      onClick={() => tu('faellig_push', async () => { const r = await api.automatikJetzt('zahlung_faellig'); return r?.meldung ?? 'Lauf angestoßen.'; })}>
                Jetzt senden
              </button>
            </div>
          </div>
        );
      })()}
      {(auswahl.length > 0 || fehler || hinweis) && (
        <div className="zahlleiste">
          {fehler && <div className="zahlmeldung"><Fehlerkasten lage={fehler} /></div>}
          {hinweis && <div className="zahlmeldung hinweis">✅ {hinweis} <button className="knopf klein" onClick={() => setHinweis(null)}>OK</button></div>}
          {auswahl.length > 0 && <>
          {d?.revolut?.saldo && summe > d.revolut.saldo.betrag && <span className="merker fehler">Nicht gedeckt: Kontostand {euro(d.revolut.saldo.betrag)}</span>}
          <span><b>{auswahl.length}</b> Rechnung{auswahl.length === 1 ? '' : 'en'}, <b>{euro(summe)}</b>, {ueberweisungenZahl} Überweisung{ueberweisungenZahl === 1 ? '' : 'en'}</span>
          <button className="knopf haupt" disabled={!bereitFuerRevolut || !!laeuft}
                  title={!d?.revolut?.bereit ? 'Revolut ist noch nicht eingerichtet.' : ''}
                  onClick={() => tu('revolut', async () => {
                    const r = await api.partnerAnRevolut([...gewaehlt]); setGewaehlt(new Set());
                    return `In Revolut liegt ein Entwurf: ${r.ueberweisungen} Überweisung${r.ueberweisungen === 1 ? '' : 'en'} für ${r.rechnungen} Rechnung${r.rechnungen === 1 ? '' : 'en'}, zusammen ${euro(r.summe)}. Jetzt in der Revolut-App freigeben.`
                      + (r.uebersprungen.length ? ` Nicht dabei: ${r.uebersprungen.join('; ')}` : '');
                  })}>
            {laeuft === 'revolut' ? 'Einen Moment' : knopfText}
          </button>
          <button className="knopf" disabled={!!laeuft} onClick={() => setBezahlt([...gewaehlt])}>Als bezahlt markieren</button>
          <button className="knopf" disabled={!!laeuft} title="Erledigt, ohne Zahlung: Altlasten, die sich nicht zuordnen lassen."
                  onClick={() => setAbschluss([...gewaehlt])}>Abschließen</button>
          <button className="knopf klein" onClick={() => setGewaehlt(new Set())}>Auswahl leeren</button>
          </>}
        </div>
      )}

      {entwuerfe.length > 0 && (
        <div className="hinweis" style={{ marginBottom: 12 }}>
          <b>In Revolut ({entwuerfe.length}):</b>
          {entwuerfe.map((u) => (
            <div key={u.id} className="klein">{u.firma ?? u.empfaenger}: {euro(u.betrag)}, {u.verwendungszweck}, {u.bank_zustand ?? 'Entwurf'}</div>
          ))}
        </div>
      )}

      {d && !gruppen.length && <Leer text={filter === 'ueberfaellig' ? 'Nichts überfällig.' : 'Keine offene Rechnung zu bezahlen.'} />}
      {(filter === 'alle' || filter === 'ueberfaellig') && gruppen.map((g) => {
        const ids = g.rechnungen.filter(istGeprueft).map((r: any) => r.id);
        const alleAn = ids.length > 0 && ids.every((id: string) => gewaehlt.has(id));
        const ungeprueft = g.rechnungen.filter((r: any) => !istGeprueft(r));
        /* NACH FÄLLIGKEIT (.406). ORIGINALTEXT MARVIN (19.10.2026): „die
           Rechnungen stehen nicht nach dem Fälligkeitsdatum sortiert, sondern
           nach geprüft wahrscheinlich. Du musst die nach dem Fälligkeitsdatum
           sortieren." Vorher geprüfte zuerst, dann der Rest; jetzt die
           älteste Fälligkeit zuerst, ohne Datum ans Ende, bei gleichem Tag
           die Nummer. */
        const reihe = [...g.rechnungen].sort((a: any, b: any) =>
          String(a.faellig_am ?? '9999').localeCompare(String(b.faellig_am ?? '9999'))
          || String(a.rechnungsnummer ?? '').localeCompare(String(b.rechnungsnummer ?? ''), 'de', { numeric: true }));
        const angehakt = g.rechnungen.filter((r: any) => gewaehlt.has(r.id));
        return (
          <div key={g.subunternehmer_id ?? 'ohne'} className={`zahlgruppe ${g.warnungen.length ? 'warnung' : ''}`}>
            <div className="zahlgruppenkopf">
              <label className="zahlwahl" style={{ flex: '1 1 auto' }}>
                <input type="checkbox" checked={alleAn} onChange={(e) => umschalten(ids, e.target.checked)} aria-label="Alle Rechnungen dieses Partners" />
                <div>
                  <div className="freigabefirma">{g.firma}</div>
                  <div className="klein">
                    {g.empfaenger
                      ? <>✓ {g.empfaenger.name}, {g.empfaenger.iban_lesbar}</>
                      : g.vorschlag
                        ? <span className="fehlertext">Bankverbindung neu: {g.vorschlag.iban_lesbar} (aus Rechnung {g.vorschlag.aus ?? ''}). Einmal bestätigen.</span>
                        : <span className="fehlertext">Keine Bankverbindung bekannt.</span>}
                    {g.subunternehmer_id && (
                      <button className="knopf klein" style={{ marginLeft: 8 }} onClick={() => setBestaetigen(g)}>
                        {g.empfaenger ? 'Bankverbindung aktualisieren' : 'Bankverbindung bestätigen'}
                      </button>
                    )}
                  </div>
                </div>
              </label>
              <div style={{ textAlign: 'right' }}>
                <div className="freigabebetrag">{euro(g.summe)}</div>
                <div className="klein leise">{g.rechnungen.length} offen{angehakt.length ? `, ${angehakt.length} gewählt` : ''}</div>
                <div className="klein" style={{ color: 'var(--erledigt)' }}>geprüft {euro(summeVon(g.rechnungen.filter(istGeprueft)))}</div>
                {ungeprueft.length > 0 && <div className="klein fehlertext">nicht geprüft {euro(summeVon(ungeprueft))} ({ungeprueft.length})</div>}
              </div>
            </div>
            {g.warnungen.map((w: string, i: number) => <div key={i} className="klein fehlertext" style={{ marginTop: 4 }}>! {w}</div>)}
            {/* NICHT IN DER ZAHLUNG, WEIL IN KORREKTUR (.538). Marvin, RE1338: „Wo ist
                diese Rechnung jetzt? Warum wird sie mir nicht angezeigt?" Hier. */}
            {!!g.in_korrektur?.length && (
              <div className="hinweis klein" style={{ marginTop: 6 }}>
                <b>Nicht in der Zahlung, Korrektur bei der Firma offen:</b>{' '}
                {g.in_korrektur.map((k: any, i: number) => (
                  <span key={k.id}>{i > 0 ? ', ' : ''}<button type="button" className="alslink" onClick={() => setPruefe(k.id)}>{k.rechnungsnummer ?? 'ohne Nummer'}</button> (seit {datum(k.korrektur_seit)}{k.geprueft ? ', geprüft' : ''})</span>
                ))}
                . Sie kommen, sobald die neue Rechnung da ist, oder sobald sie im Prüffenster als geprüft abgehakt oder die Korrektur zurückgenommen wird.
              </div>
            )}
            {/* VERRECHNUNG (.480): geht von der nächsten Überweisung an diese Firma ab. */}
            {g.verrechnung?.summe > 0 && (
              <div className="hinweis klein" style={{ marginTop: 6 }}>
                Wird mit der nächsten Überweisung verrechnet: <b>− {euro(g.verrechnung.summe)}</b>
                {g.verrechnung.zeilen.map((v: any) => (
                  <div key={v.id} className="leise">{euro(v.betrag)}, {v.grund}
                    <button type="button" className="knopf klein" style={{ marginLeft: 6 }} disabled={!!laeuft}
                            onClick={async () => { if (await fragen('Diese Verrechnung zurücknehmen?')) tu('verr' + v.id, async () => { await api.partnerVerrechnungLoeschen(v.id); return 'Verrechnung zurückgenommen.'; }); }}>zurücknehmen</button>
                  </div>
                ))}
              </div>
            )}
            {angehakt.length > 1 && (() => {
              const z = zwecke(angehakt.map((r: any) => r.rechnungsnummer));
              return (
                <div className="klein" style={{ marginTop: 6 }}>
                  Sammelüberweisung: <b>{euro(Math.max(0.01, angehakt.reduce((s: number, r: any) => s + Number(r.zahlbetrag ?? r.betrag), 0) - (g.verrechnung?.summe ?? 0)))}</b>
                  {z.length > 1 ? `, in ${z.length} Überweisungen, damit jede Rechnungsnummer im Verwendungszweck steht:` : ', Verwendungszweck:'}
                  {z.map((t, i) => <div key={i} className="leise">„{t}"</div>)}
                </div>
              );
            })()}
            <table>
              <thead><tr><th></th><th>Rechnung</th><th>vom</th><th>Baustelle</th><th>fällig</th><th>Prüfung</th><th className="zahl">Betrag</th></tr></thead>
              <tbody>
                {reihe.map((r: any) => (
                  <tr key={r.id} className={istGeprueft(r) ? '' : 'ungeprueft'}>
                    <td><input type="checkbox" checked={gewaehlt.has(r.id)} disabled={!istGeprueft(r)}
                               title={istGeprueft(r) ? '' : 'Erst prüfen: bezahlt wird nur, was geprüft ist.'}
                               onChange={(e) => umschalten([r.id], e.target.checked)} aria-label={`Rechnung ${r.rechnungsnummer ?? ''}`} /></td>
                    <td>{r.hat_datei
                          ? <a href={api.rechnungAdresse(r.id, r.dateiname)} target="_blank" rel="noreferrer" title="Rechnung ansehen">{r.rechnungsnummer ?? 'ohne Nummer'}</a>
                          : (r.rechnungsnummer ?? 'ohne Nummer')}{r.iban_abweichend && <span className="merker fehler klein" style={{ marginLeft: 6 }}>andere IBAN</span>}</td>
                    <td>{datum(r.rechnungsdatum ?? r.eingang_am)}</td>
                    {/* DIE BAUSTELLE ÖFFNET DEN AUFTRAG (.470). Marvin, 23.10.2026: „die Adresse
                        zum Beispiel kann ich hier nicht anklicken ... ich will ja auch den Auftrag öffnen." */}
                    <td>{r.auftrag_id && r.anschrift
                          ? <a href={`#/akte/${r.auftrag_id}`} title="Auftrag öffnen">{r.anschrift}</a>
                          : (r.anschrift ?? '')}</td>
                    <td className={r.faellig_am && r.faellig_am < heute ? 'fehlertext' : ''}>{r.faellig_am ? datum(r.faellig_am) : ''}</td>
                    <td>{istGeprueft(r) ? <span style={{ color: 'var(--erledigt)', fontWeight: 600 }}>✓ geprüft</span>
                      : <button type="button" className="knopf klein pruefen-rot" onClick={() => setPruefe(r.id)}>Prüfen →</button>}</td>
                    <td className="zahl">
                      {/* SKONTO (.480): Rechnungsbetrag, Abzug laut Rechnung, Zahlbetrag. */}
                      {r.skonto_betrag > 0 ? (
                        <>
                          <div className="leise" style={{ textDecoration: 'line-through' }}>{euro(r.betrag)}</div>
                          <div className="klein" title={r.skonto_text ?? ''}>− {String(r.skonto_prozent).replace('.', ',')} % Skonto {euro(r.skonto_betrag)}
                            {r.skonto_bis ? <span className={r.skonto_abgelaufen ? 'fehlertext' : 'leise'}>, {r.skonto_abgelaufen ? 'Frist war' : 'bis'} {datum(r.skonto_bis)}</span> : null}</div>
                          <b>{euro(r.zahlbetrag)}</b>
                        </>
                      ) : euro(r.betrag)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* OHNE SKONTO BEZAHLT (.480). Marvin, 23.10.2026: „Ich habe Brester überwiesen, ohne dass ich
          die 7% einbehalten habe ... dann müssen wir das bei der nächsten Rechnung in den Summen abziehen." */}
      {(d?.ohne_skonto ?? []).length > 0 && (
        <OhneSkonto zeilen={d!.ohne_skonto!} laeuft={laeuft}
                    los={(ids) => tu('skonto', async () => { const r = await api.partnerSkontoNachholen(ids); return `Vorgemerkt: ${euro(r.summe)} werden mit der nächsten Überweisung verrechnet.`; })} />
      )}

      {zuletzt.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary className="klein">Zuletzt überwiesen ({zuletzt.length})</summary>
          {zuletzt.map((u) => (
            <div key={u.id} className="klein" style={{ marginTop: 4 }}>
              {u.firma ?? u.empfaenger}: {euro(u.betrag)}, {u.verwendungszweck}, {u.zustand === 'bezahlt' ? `bezahlt am ${datum(u.bezahlt_am)}` : `abgebrochen: ${u.fehler ?? ''}`}
            </div>
          ))}
        </details>
      )}

      {pruefe && <Prueffenster ich={ich} id={pruefe} zu={() => setPruefe(null)} fertig={() => { laden(); }} />}
      {bestaetigen && <BankBestaetigen g={bestaetigen} zu={() => setBestaetigen(null)} fertig={() => { setBestaetigen(null); setHinweis('Bankverbindung bestätigt und gemerkt.'); laden(); }} />}
      {abschluss && <AbschlussDialog anzahl={abschluss.length} zu={() => setAbschluss(null)} los={async (grund) => {
        const ids = abschluss; setAbschluss(null);
        await tu('abschluss', async () => { const r = await api.partnerAbschliessen(ids, grund); setGewaehlt(new Set()); return `${r.abgeschlossen} Rechnungen abgeschlossen.`; });
      }} />}
      {bezahlt && <BezahltDialog anzahl={bezahlt.length} zu={() => setBezahlt(null)} los={async (tag) => {
        const ids = bezahlt; setBezahlt(null);
        await tu('bezahlt', async () => { const r = await api.partnerBezahlt(ids, tag); setGewaehlt(new Set()); return `${r.markiert} Rechnungen als bezahlt markiert.`; });
      }} />}
    </Karte>
    </>
  );
}

function ibanOk(roh: string): boolean {
  const iban = roh.replace(/[\s.\-]/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban) || (iban.startsWith('DE') && iban.length !== 22)) return false;
  let rest = 0;
  for (const z of iban.slice(4) + iban.slice(0, 4)) {
    const w = /\d/.test(z) ? z : String(z.charCodeAt(0) - 55);
    for (const c of w) rest = (rest * 10 + Number(c)) % 97;
  }
  return rest === 1;
}

function BankBestaetigen({ g, zu, fertig }: { g: any; zu: () => void; fertig: () => void }) {
  const start = g.empfaenger ?? g.vorschlag ?? { name: g.firma, iban_lesbar: '', bic: '' };
  const [name, setName] = useState<string>(start.name ?? g.firma ?? '');
  const [iban, setIban] = useState<string>(start.iban_lesbar ?? '');
  const [bic, setBic] = useState<string>(start.bic ?? '');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const stimmt = ibanOk(iban);
  return (
    <Dialog titel="Bankverbindung bestätigen" untertitel={g.firma} breite={620} zu={zu}>
      <form onSubmit={async (e) => {
        e.preventDefault(); if (!stimmt) return;
        setLaeuft(true); setFehler(null);
        try { await api.partnerEmpfaenger({ subunternehmer_id: g.subunternehmer_id, name, iban, bic: bic || null }); fertig(); }
        catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
      }}>
        <p style={{ marginTop: 0 }}>Bitte mit der Rechnung oder einer Mitteilung des Partners vergleichen. Ab dann geht jede Überweisung an diese Verbindung, und eine abweichende IBAN in einer Rechnung wird rot gemeldet.</p>
        <Fehlerkasten lage={fehler} />
        <div className="reihe" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Empfaengerwahl art="partner" titel="Kontoinhaber" wert={name} setze={setName}
                          waehle={(e) => { setName(e.name); setIban(e.iban_lesbar); setBic(e.bic ?? ''); }} breite={560} />
          <Text name="iban" titel="IBAN" wert={iban} setze={setIban} hoechstens={40} breite={360} fehler={fehler}
                hinweis={iban.trim() ? (stimmt ? 'Prüfziffer stimmt.' : 'Die Prüfziffer stimmt nicht.') : undefined} />
          <Text name="bic" titel="BIC (freiwillig)" wert={bic} setze={setBic} hoechstens={20} breite={180} fehler={fehler} />
        </div>
        <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
          <button className="knopf haupt" disabled={laeuft || !stimmt || name.trim().length < 3}>Bestätigen</button>
          <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}

function BezahltDialog({ anzahl, zu, los }: { anzahl: number; zu: () => void; los: (tag: string) => Promise<void> }) {
  const [tag, setTag] = useState(heuteIso());
  return (
    <Dialog titel={`${anzahl} Rechnung${anzahl === 1 ? '' : 'en'} als bezahlt markieren`} breite={440} zu={zu}>
      <form onSubmit={(e) => { e.preventDefault(); if (tag) los(tag); }}>
        <p style={{ marginTop: 0 }}>Für Zahlungen, die nicht über den Entwurf in Revolut liefen.</p>
        <Text name="tag" titel="Bezahlt am" art="date" wert={tag} setze={setTag} breite={200} />
        <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
          <button className="knopf haupt" disabled={!tag}>Markieren</button>
          <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}

/* ABSCHLIESSEN (.269): Altlasten, die sich nicht zuordnen lassen, gelten als
   erledigt, ohne dass eine Zahlung behauptet wird. */
function AbschlussDialog({ anzahl, zu, los }: { anzahl: number; zu: () => void; los: (grund: string) => Promise<void> }) {
  const [grund, setGrund] = useState('Altlast, nicht zuordenbar');
  return (
    <Dialog titel={`${anzahl} Rechnung${anzahl === 1 ? '' : 'en'} abschließen`} breite={520} zu={zu}>
      <form onSubmit={(e) => { e.preventDefault(); if (grund.trim()) los(grund.trim()); }}>
        <p style={{ marginTop: 0 }}>Sie gelten danach als erledigt und verschwinden aus „zu bezahlen" und „überfällig". Eine Zahlung wird nicht vermerkt; der Grund steht an jeder Rechnung.</p>
        <Text name="grund" titel="Grund" wert={grund} setze={setGrund} hoechstens={300} breite={460} />
        <div className="reihe" style={{ gap: 8, marginTop: 12 }}>
          <button className="knopf haupt" disabled={!grund.trim()}>Abschließen</button>
          <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}

/* EIN VORSCHLAG AUS DEM KONTO (.261): die genannten Rechnungen einzeln an- und
   abhaken, die Summe rechnet mit. Vorgewählt ist, was dem Betrag am nächsten
   kommt; eine Nummer, die mehrfach erfasst ist, trägt die Marke „doppelt". */
function Vorschlag({ v, laeuft, los }: { v: any; laeuft: boolean; los: (uebernehmen: boolean, ids?: string[]) => void }) {
  const genannt: any[] = v.genannt ?? [];
  const [an, setAn] = useState<Set<string>>(new Set(genannt.filter((r) => r.vorgewaehlt && !r.bezahlt_am).map((r) => r.id)));
  const summe = genannt.filter((r) => an.has(r.id)).reduce((s, r) => s + (Number(r.betrag) || 0), 0);
  const diff = Math.round((Number(v.betrag) - summe) * 100) / 100;
  return (
    <div className="zahlgruppe">
      <div className="zahlgruppenkopf">
        <div>
          <div className="freigabefirma">{euro(v.betrag)} am {datum(v.datum)}</div>
          <div className="klein">{v.zweck}</div>
          <div className="klein fehlertext">{v.grund}</div>
        </div>
        <div className="reihe" style={{ gap: 6 }}>
          <button className="knopf haupt klein" disabled={laeuft || !an.size} onClick={() => los(true, [...an])}>
            Übernehmen ({an.size})
          </button>
          <button className="knopf klein" disabled={laeuft} onClick={() => los(false)}>Verwerfen</button>
        </div>
      </div>
      <table>
        <tbody>
          {genannt.map((r) => (
            <tr key={r.id}>
              <td style={{ width: 28 }}><input type="checkbox" disabled={!!r.bezahlt_am} checked={an.has(r.id)}
                onChange={(e) => setAn((s) => { const n = new Set(s); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })} /></td>
              <td>{r.dateiname ? <a href={api.rechnungAdresse(r.id, r.dateiname)} target="_blank" rel="noreferrer">{r.nr}</a> : r.nr}{r.doppelt && <span className="merker warnung klein" style={{ marginLeft: 6 }}>doppelt</span>}{r.bezahlt_am && <span className="merker gruen klein" style={{ marginLeft: 6 }}>schon bezahlt</span>}</td>
              <td>{r.firma ?? <span className="leise">ohne Firma</span>}</td>
              <td className="klein leise">{r.rechnungsdatum ? datum(r.rechnungsdatum) : ''}</td>
              <td className="zahl">{euro(r.betrag)}</td>
            </tr>
          ))}
          <tr>
            <td></td><td colSpan={3}><b>Gewählt</b>{Math.abs(diff) >= 0.01
              ? <span className="fehlertext">, {diff > 0 ? 'überwiesen mehr' : 'überwiesen weniger'}: {euro(Math.abs(diff))} ({prozent(diff, summe)})</span>
              : <span style={{ color: 'var(--erledigt)' }}>, passt genau</span>}</td>
            <td className="zahl"><b>{euro(summe)}</b></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}


/** Bezahlt, ohne dass das Skonto laut Rechnung gezogen wurde (.480): wählen und vormerken. */
function OhneSkonto({ zeilen, laeuft, los }: { zeilen: any[]; laeuft: string | null; los: (ids: string[]) => void }) {
  /* GEGEN DAS KONTO GEPRÜFT (.514): angehakt ist nur, was laut Konto wirklich
     voll bezahlt wurde. Ohne zugeordneten Kontoumsatz bleibt der Haken weg,
     die Zeile sagt, dass im Konto nachzusehen ist. */
  const [an, setAn] = useState<Set<string>>(() => new Set(zeilen.filter((z) => z.konto_geprueft !== false).map((z) => z.id)));
  const summe = zeilen.filter((z) => an.has(z.id)).reduce((s, z) => s + Number(z.skonto_betrag || 0), 0);
  const ungeprueft = zeilen.filter((z) => z.konto_geprueft === false).length;
  return (
    <div className="zahlgruppe warnung" style={{ marginTop: 8 }}>
      <div className="freigabefirma">Ohne Skonto bezahlt ({zeilen.length})</div>
      <p className="klein leise" style={{ margin: '2px 0 6px' }}>Auf diesen Rechnungen steht Skonto, und laut Kontoumsatz wurde der volle Betrag überwiesen. Rechnungen, bei denen das Konto den Abzug zeigt, stehen hier nicht mehr. Vorgemerkt geht der Abzug von der nächsten Überweisung an die Firma ab.{ungeprueft > 0 && <> <b>{ungeprueft} ohne zugeordneten Kontoumsatz</b>: erst im Konto nachsehen, dann anhaken.</>}</p>
      <table>
        <thead><tr><th></th><th>Firma</th><th>Rechnung</th><th>bezahlt am</th><th>Skonto laut Rechnung</th><th>Konto</th><th className="zahl">Betrag</th><th className="zahl">Abzug</th></tr></thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.id}>
              <td><input type="checkbox" checked={an.has(z.id)} onChange={(e) => setAn((alt) => { const n = new Set(alt); if (e.target.checked) n.add(z.id); else n.delete(z.id); return n; })} aria-label={`Rechnung ${z.rechnungsnummer ?? ''}`} /></td>
              <td>{z.firma ?? ''}</td>
              <td>{z.rechnungsnummer ?? 'ohne Nummer'}</td>
              <td>{datum(z.bezahlt_am)}</td>
              <td title={z.skonto_text ?? ''}>{String(z.skonto_prozent).replace('.', ',')} %</td>
              <td className={'klein' + (z.konto_geprueft === false ? ' warnung-text' : '')} style={z.konto_geprueft === false ? { color: 'var(--warnung, #b45309)' } : undefined}>{z.konto ?? ''}</td>
              <td className="zahl">{euro(z.betrag)}</td>
              <td className="zahl"><b>{euro(z.skonto_betrag)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="reihe" style={{ gap: 8, marginTop: 6, alignItems: 'center' }}>
        <button type="button" className="knopf haupt klein" disabled={!!laeuft || !an.size} onClick={() => los([...an])}>
          {laeuft === 'skonto' ? 'Einen Moment' : `Beim nächsten Mal abziehen (${euro(summe)})`}
        </button>
      </div>
    </div>
  );
}
